import { useState } from "react";

const checklist = [
  { item: "Warm beige or oat upholstery", note: "Avoid cool grey-beige tones" },
  { item: "Natural fibre fabric", note: "Linen, cotton-linen blend, or boucle" },
  { item: "Soft rounded silhouette", note: "Low back, gentle arms, relaxed form" },
  { item: "Light wood or warm metal legs", note: "Oak, walnut, or brushed brass" },
  { item: "Layered cushions in warm neutrals", note: "Oat, sand, ivory — 2 to 3 tones" },
  { item: "Linen or knit throw", note: "Draped loosely over one arm" },
  { item: "Neutral rug underneath", note: "Grounds the sofa and defines the space" },
  { item: "Warm-toned floor or table lamp nearby", note: "2700K bulb for the warmest effect" },
];

const faqs = [
  {
    q: "What is the best fabric for a beige sofa?",
    a: "Linen and cotton-linen blends are the most popular choices for a calm, natural look. They breathe well, age gracefully, and have a soft texture that suits a relaxed interior. Boucle is a great option if you want more visual texture and a slightly more elevated feel. Avoid synthetic fabrics if you want the sofa to feel genuinely warm and natural.",
  },
  {
    q: "Will a beige sofa show stains easily?",
    a: "It depends on the fabric and finish. Performance linen and treated cotton blends are much more practical than untreated natural fibres. Many mid-range and higher-end sofas now come with stain-resistant treatments that do not affect the look or feel of the fabric. If you have children or pets, look for sofas with removable, washable covers.",
  },
  {
    q: "What colours go well with a beige sofa?",
    a: "Warm neutrals work best — think terracotta, warm white, soft sage, and warm wood tones. Avoid cool colours like grey-blue or stark white, which can make the beige look flat. The goal is to stay within a warm tonal family and let texture do the work of adding interest.",
  },
  {
    q: "How do I make a beige sofa look expensive?",
    a: "Focus on layering and material quality. A few well-chosen cushions in warm neutral tones, a linen throw, and a textured rug underneath will elevate even a budget sofa significantly. Warm lighting nearby also makes a huge difference — a floor lamp with a 2700K bulb brings out the warmth in the upholstery and makes the whole room feel more considered.",
  },
];

export default function BSSFormula() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-0 pb-16">

      {/* Checklist */}
      <div className="mb-16">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          The Formula
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          The Beige Sofa Checklist
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="space-y-3">
          {checklist.map((c, i) => (
            <div key={i} className="flex items-start gap-4 border-b border-[#EDE6DA] pb-3 last:border-0">
              <div className="w-5 h-5 flex items-center justify-center rounded-full border border-[#C8BAA8] flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-[#C8BAA8] text-[10px]" />
              </div>
              <div>
                <p
                  className="text-[#2C2825] text-sm"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
                >
                  {c.item}
                </p>
                <p
                  className="text-[#B5A898] text-xs mt-0.5"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                >
                  {c.note}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="mb-16">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          FAQ
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          Frequently Asked Questions
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-[#EDE6DA] rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#F5F1E8]/50 transition-colors duration-200"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span
                  className="text-[#2C2825] text-sm leading-snug pr-4"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
                >
                  {faq.q}
                </span>
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-add-line text-[#8B8378] text-base transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`}
                  />
                </span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5">
                  <p
                    className="text-[#6B5E53] text-sm leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                  >
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Soft Conclusion */}
      <div className="mb-4">
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <p
          className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          A beige sofa is not a compromise — it is a considered choice.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          When you choose the right tone, material, and scale, a beige sofa becomes the quiet foundation
          that makes everything else in the room feel more intentional. Layer it with warm textures,
          natural materials, and soft light — and the result is a living room that feels genuinely calm and complete.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          Take your time choosing. The right sofa will still feel right in ten years.
        </p>
      </div>

    </div>
  );
}
